import { faCamera, faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState } from 'react';
import { uploadPhotoToImmich } from '@/api/immich';
import { useTripContext } from '@/contexts/TripContext';
import { ImmichImage } from '@/design-system/primitives/ImmichImage';
import { PhotoLightbox } from '@/design-system/primitives/PhotoLightbox';
import type { ItineraryItem } from '@/types/itinerary';
import type { Trip } from '@/types/trip';
import { twx } from '@/utils/tailwind';

export interface PlacePhotosCornerProps {
  item: ItineraryItem;
  trip: Trip;
  /** When true, the tile becomes interactive: empty state opens the
   *  file picker; populated tile opens the lightbox with an in-modal
   *  "add more" button. When false, the tile is purely a preview that
   *  taps into a read-only lightbox; the empty case suppresses itself
   *  entirely. */
  canEdit: boolean;
  /** Extra classes for the outer tile wrapper — typically used to apply
   *  `float-right` or sizing overrides from the parent context. */
  className?: string;
}

/**
 * Compact single-thumbnail tile that represents a place's photo set in
 * the corner of a TripPlaceRow. Always one tile high; floats nicely on
 * the row's right side so the title + metadata wrap around it.
 *
 *  - Empty + canEdit → dashed border + camera glyph; tap opens the OS
 *    file picker.
 *  - Empty + read-only → nothing renders.
 *  - Has photos → first photo as cover-fit thumbnail with a count badge
 *    when there are more; tap opens the fullscreen lightbox.
 *  - Lightbox in canEdit mode exposes "add more" + per-photo "remove"
 *    affordances so the corner tile stays a single tile while still
 *    surfacing every action.
 */
export const PlacePhotosCorner = ({ item, trip, canEdit, className }: PlacePhotosCornerProps) => {
  const { attachPhotoToPlace, removePhotoFromPlace, setTripImmichAlbumId } = useTripContext();
  const place = trip.places.find((p) => p.item.id === item.id);
  const photos = place?.immichPhotos ?? [];
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (photos.length === 0 && !canEdit) return null;

  const handleFilesPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadInProgress(true);
    setUploadError(null);
    try {
      // Sequential — the first upload triggers the lazy album-create
      // on the worker; subsequent ones reuse the returned id.
      let albumId = trip.immichAlbumId;
      // When the worker has to create the album on this upload, it
      // also shares with the trip's existing collaborators so they
      // never see "Photos uploaded but I can't see them" — pre-added
      // collaborators get access at album-create time.
      // Only collaborators with a resolved Immich account get album access;
      // others still edit the trip, just without photo sync.
      const collaboratorUserIds = (trip.collaborators ?? []).flatMap((c) =>
        c.immichUserId ? [c.immichUserId] : [],
      );
      for (const file of Array.from(files)) {
        const result = await uploadPhotoToImmich({
          file,
          tripName: trip.name,
          tripLocation: trip.location,
          tripUrl: window.location.href,
          immichAlbumId: albumId,
          fileCreatedAt: file.lastModified ? new Date(file.lastModified).toISOString() : undefined,
          collaboratorUserIds,
        });
        if (result.albumCreated) {
          albumId = result.albumId;
          setTripImmichAlbumId(trip.id, result.albumId);
        }
        attachPhotoToPlace(trip.id, item.id, {
          assetId: result.assetId,
          originalFileName: result.info?.originalFileName,
          fileCreatedAt: result.info?.exifInfo?.dateTimeOriginal ?? result.info?.fileCreatedAt,
          width: result.info?.exifInfo?.exifImageWidth,
          height: result.info?.exifInfo?.exifImageHeight,
          uploadedAt: Date.now(),
        });
      }
    } catch (err) {
      console.error('[immich] upload failed', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadInProgress(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTileTap = () => {
    if (photos.length > 0) {
      setLightboxOpen(true);
    } else if (canEdit) {
      fileInputRef.current?.click();
    }
  };

  // Empty + editable tile — dashed, camera glyph, opens picker on tap.
  if (photos.length === 0) {
    return (
      <>
        <button
          type="button"
          onClick={handleTileTap}
          disabled={uploadInProgress}
          aria-label="Add photos"
          title="Add photos to this place"
          className={twx(
            'block w-10 h-10 rounded-lg border-2 border-dashed flex items-center justify-center transition-colors shrink-0',
            uploadInProgress
              ? 'border-content-border text-content-disabled cursor-wait'
              : 'border-content-border text-content-subtle hover:border-secondary-main hover:text-secondary-emphasized hover:bg-content-focal',
            className,
          )}
        >
          <FontAwesomeIcon
            icon={uploadInProgress ? faSpinnerThird : faCamera}
            spin={uploadInProgress}
            style={{ width: 16, height: 16 }}
          />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFilesPicked}
        />
        {uploadError && <p className="mt-1 text-[10px] text-error-text">{uploadError}</p>}
      </>
    );
  }

  // Populated tile — first photo as cover, optional count badge for the rest.
  const first = photos[0];
  const extra = photos.length - 1;
  return (
    <>
      <button
        type="button"
        onClick={handleTileTap}
        aria-label={`View ${photos.length} photo${photos.length === 1 ? '' : 's'}`}
        title={`View ${photos.length} photo${photos.length === 1 ? '' : 's'}`}
        className={twx(
          'relative w-10 h-10 rounded-lg overflow-hidden bg-content-focal shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary-emphasized hover:opacity-90 transition-opacity',
          className,
        )}
      >
        {first && (
          <ImmichImage
            assetId={first.assetId}
            size="preview"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        {extra > 0 && (
          <span
            aria-hidden
            className="absolute bottom-0.5 right-0.5 inline-flex items-center justify-center min-w-[16px] h-[14px] px-0.5 rounded-full bg-black/70 text-white text-[9px] font-semibold leading-none"
          >
            +{extra}
          </span>
        )}
        {uploadInProgress && (
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-black/40"
          >
            <FontAwesomeIcon
              icon={faSpinnerThird}
              spin={true}
              style={{ width: 16, height: 16, color: 'white' }}
            />
          </span>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFilesPicked}
      />
      {uploadError && <p className="mt-1 text-[10px] text-error-text">{uploadError}</p>}
      {lightboxOpen && (
        <PhotoLightbox
          // "<TRIP NAME> – <PLACE NAME>" — matches the trip-level
          // aggregator caption for place-attached photos, so the same
          // photo shows the same label whether opened from the row
          // tile or the trip-header tile.
          photos={photos.map((p) => ({
            assetId: p.assetId,
            caption: `${trip.name} – ${item.name}`,
          }))}
          onClose={() => setLightboxOpen(false)}
          onAddPhotos={canEdit ? () => fileInputRef.current?.click() : undefined}
          onRemovePhoto={
            canEdit ? (assetId) => removePhotoFromPlace(trip.id, item.id, assetId) : undefined
          }
        />
      )}
    </>
  );
};
